import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import { API_URLS, API_BASE_URL } from "../../constants/api-urls";
import "./MyBids.scss";

export default function MyBids() {
  const navigate = useNavigate();
  const [allBids, setAllBids] = useState([]); // All bids (unfiltered)
  const [bids, setBids] = useState([]); // Filtered bids for display
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [inspectionRFQStatus, setInspectionRFQStatus] = useState({}); // Map of auctionId -> RFQ status
  const [inspectionQuotesCount, setInspectionQuotesCount] = useState({}); // Map of auctionId -> quotes_received_count (only show "View QA Quotations" when > 0)
  const [inspectionP2pPayment, setInspectionP2pPayment] = useState({});
  const [inspectionDecisionByAuction, setInspectionDecisionByAuction] = useState({}); // Map of auctionId -> 'accept' | 'reject' | null (only after inspection completed)

  useEffect(() => {
    fetchBids();
  }, []);

  useEffect(() => {
    // Apply filter when filterStatus or allBids changes
    if (filterStatus !== "all") {
      const filtered = allBids.filter((b) => {
        if (filterStatus === "active") {
          return b.bid_status === "active" || b.bid_status === "pending";
        }
        if (filterStatus === "won") {
          return isWonBid(b);
        }
        if (filterStatus === "lost") {
          return b.bid_status === "lost" || b.bid_status === "outbid";
        }
        if (filterStatus === "rejected") {
          return b.bid_status === "rejected";
        }
        return true;
      });
      setBids(filtered);
    } else {
      setBids(allBids);
    }
  }, [filterStatus, allBids]);

  const fetchBids = async () => {
    try {
      setLoading(true);
      const url = API_URLS.AUCTION.MY_BIDS;
      console.log("MyBids - Fetching from URL:", url);
      console.log("MyBids - Full API URL:", `${API_BASE_URL}${url}`);
      
      const response = await CommonService.get(url);

      console.log("MyBids - API Response:", response);

      if (response && response.success && response.data) {
        let bidsData = Array.isArray(response.data) ? response.data : [];
        
        // Map the data to ensure consistent field names
        bidsData = bidsData.map((bid) => ({
          ...bid,
          // Ensure all required fields are present
          bid_id: bid.bid_id || bid.id,
          bid_amount: parseFloat(bid.bid_amount || bid.amount || 0) || 0, // Ensure it's a number
          bid_status: bid.bid_status || bid.status || 'active',
          is_highest_bid: bid.is_highest_bid || bid.is_highest || 'N',
          bid_placed_at: bid.bid_placed_at || bid.created_at || bid.bid_date,
          product_name: bid.product_name || 'Unknown Product',
          auction_id: bid.auction_id || bid.auctionId,
          payment_status: bid.payment_status ?? bid.paymentStatus ?? null,
          winning_bid_id: bid.winning_bid_id ?? bid.winningBidId ?? null,
          inspection_allowed: bid.inspection_allowed ?? bid.inspectionAllowed ?? null,
          inspector_type: bid.inspector_type ?? bid.inspectorType ?? null,
          auction: {
            ...bid.auction,
            status: bid.auction_status || bid.auction?.status || 'active',
            end_date: bid.auction?.end_date || bid.end_date
          }
        }));
        
        setAllBids(bidsData);
        
        // Apply initial filter
        if (filterStatus !== "all") {
          const filtered = bidsData.filter((b) => {
            if (filterStatus === "active") {
              return b.bid_status === "active" || b.bid_status === "pending";
            }
            if (filterStatus === "won") {
              return isWonBid(b);
            }
            if (filterStatus === "lost") {
              return b.bid_status === "lost" || b.bid_status === "outbid";
            }
            if (filterStatus === "rejected") {
              return b.bid_status === "rejected";
            }
            return true;
          });
          setBids(filtered);
        } else {
          setBids(bidsData);
        }
        
        console.log("MyBids - Processed bids:", bidsData);
        
        // Fetch inspection RFQ status for won bids with completed payment
        fetchInspectionRFQStatus(bidsData);
      } else {
        console.log("MyBids - No data in response");
        setAllBids([]);
        setBids([]);
      }
    } catch (error) {
      console.error("Error fetching bids:", error);
      console.error("Error response:", error.response);
      console.error("Error status:", error.response?.status);
      console.error("Error data:", error.response?.data);
      console.error("Error message:", error.message);
      console.error("Full error object:", JSON.stringify(error.response?.data || error, null, 2));
      
      let errorMessage = "Failed to fetch bids. Please try again.";
      if (error.response?.status === 401) {
        errorMessage = "Authentication required. Please log in again.";
      } else if (error.response?.status === 404) {
        errorMessage = "Endpoint not found (404). The backend server may need to be restarted after the latest build. Please check the console for the full URL being called.";
        console.error("404 Error - Full URL attempted:", error?.config?.baseURL + error?.config?.url);
      } else if (error.response?.status === 400) {
        // Show the actual error message from backend for 400 errors
        const backendMessage = error.response?.data?.message || error.response?.data?.error || "Bad Request";
        errorMessage = `Bad Request (400): ${backendMessage}`;
        console.error("400 Error Details:", {
          message: backendMessage,
          data: error.response?.data,
          fullResponse: error.response
        });
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      setAllBids([]);
      setBids([]);
    } finally {
      setLoading(false);
    }
  };

  // Treat bid as "won" if status is won or this bid is the auction's winning bid
  const isWonBid = (bid) =>
    bid.bid_status === "won" ||
    bid.bid_status === "accepted" ||
    (bid.winning_bid_id != null && bid.bid_id === bid.winning_bid_id);

  const fetchInspectionRFQStatus = async (bidsData) => {
    // Filter won bids with completed payment
    const eligibleBids = bidsData.filter(
      (bid) => isWonBid(bid) && bid.payment_status === "completed"
    );

    // Fetch RFQ status and quotes count for each eligible bid
    const rfqStatusMap = {};
    const quotesCountMap = {};
    for (const bid of eligibleBids) {
      try {
        const response = await CommonService.get(
          API_URLS.AUCTION.GET_INSPECTION_RFQ_BY_AUCTION(bid.auction_id)
        );
        if (response && response.success && response.data) {
          rfqStatusMap[bid.auction_id] = response.data.status || null;
          quotesCountMap[bid.auction_id] = Number(response.data.quotes_received_count) || 0;
        } else {
          rfqStatusMap[bid.auction_id] = null;
          quotesCountMap[bid.auction_id] = 0;
        }
      } catch (error) {
        if (error.response?.status === 404) {
          rfqStatusMap[bid.auction_id] = null;
          quotesCountMap[bid.auction_id] = 0;
        } else {
          console.error(`Error fetching RFQ for auction ${bid.auction_id}:`, error);
          rfqStatusMap[bid.auction_id] = null;
          quotesCountMap[bid.auction_id] = 0;
        }
      }
    }
    setInspectionRFQStatus(rfqStatusMap);
    setInspectionQuotesCount(quotesCountMap);

    const p2pPaymentMap = {};
    for (const bid of eligibleBids) {
      const rfqStatus = rfqStatusMap[bid.auction_id];
      if (rfqStatus !== 'inspector_selected' && rfqStatus !== 'inspection_scheduled') continue;
      try {
        const inspRes = await CommonService.get(
          API_URLS.AUCTION.GET_INSPECTION_BY_AUCTION(bid.auction_id),
        );
        if (inspRes?.success && inspRes.data?.p2p_payment) {
          p2pPaymentMap[bid.auction_id] = inspRes.data.p2p_payment;
        }
      } catch (error) {
        if (error.response?.status !== 404) {
          console.error(`Error fetching inspection payment UX for auction ${bid.auction_id}:`, error);
        }
      }
    }
    setInspectionP2pPayment(p2pPaymentMap);

    // For auctions where inspection is completed, fetch buyer decision – "Update Collection Status" only after accept
    const decisionMap = {};
    const completedAuctionIds = eligibleBids
      .filter((b) => rfqStatusMap[b.auction_id] === "completed")
      .map((b) => b.auction_id);
    for (const auctionId of [...new Set(completedAuctionIds)]) {
      try {
        const res = await CommonService.get(API_URLS.AUCTION.GET_INSPECTION_DECISION_BY_AUCTION(auctionId));
        decisionMap[auctionId] = res?.success && res?.data?.decision ? res.data.decision : null;
      } catch (e) {
        if (e.response?.status !== 404) console.error(`Error fetching inspection decision for auction ${auctionId}:`, e);
        decisionMap[auctionId] = null;
      }
    }
    setInspectionDecisionByAuction(decisionMap);
  };

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || !isFinite(numAmount)) {
      return "₹0"; // Return "₹0" for invalid numbers
    }
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  const getBidStatusBadge = (status, isHighest) => {
    if (isHighest === "Y") {
      return <span className="status-badge highest">Highest Bid</span>;
    }
    
    const statusConfig = {
      active: { label: "Active", color: "#28a745" },
      outbid: { label: "Outbid", color: "#dc3545" },
      won: { label: "Won", color: "#28a745" },
      lost: { label: "Lost", color: "#6c757d" },
      rejected: { label: "Rejected", color: "#dc3545" },
    };

    const config = statusConfig[status] || { label: status, color: "#666" };
    return (
      <span
        className="status-badge"
        style={{ backgroundColor: config.color }}
      >
        {config.label}
      </span>
    );
  };

  const getAuctionStatus = (bid) => {
    // Check if auction object exists
    const auction = bid.auction || {};
    const auctionStatus = bid.auction_status || auction.status;
    const endDate = auction.end_date || bid.end_date;
    
    if (!auctionStatus && !endDate) {
      // Try to determine from bid status
      if (bid.bid_status === "won") return "Won";
      if (bid.bid_status === "outbid" || bid.bid_status === "lost") return "Ended";
      return "Active";
    }
    
    const now = new Date();
    if (auctionStatus === "ended" || auctionStatus === "closed") return "Ended";
    if (endDate && new Date(endDate) < now) return "Ended";
    if (auctionStatus === "active" || auctionStatus === "published") return "Active";
    return auctionStatus || "Active";
  };

  // QA flow: auction has inspection Allowed + Private Inspector, OR an inspection RFQ exists for this auction
  const hasQAFlow = (bid) =>
    (bid.inspection_allowed === "Yes" && bid.inspector_type === "Private Inspector") ||
    (inspectionRFQStatus[bid.auction_id] != null && inspectionRFQStatus[bid.auction_id] !== "");

  const showProductCollectionStatus = (bid) => {
    if (!isWonBid(bid) || bid.payment_status !== "completed") return false;
    if (!hasQAFlow(bid)) return true;
    const rfqStatus = inspectionRFQStatus[bid.auction_id];
    const decision = inspectionDecisionByAuction[bid.auction_id];
    // Before a QA RFQ exists, allow collection & delivery (matches seller My Auctions)
    if (rfqStatus == null || rfqStatus === "") {
      return true;
    }
    // After QA RFQ exists: show only after inspection completed and buyer accepted
    return rfqStatus === "completed" && (decision === "accept" || decision === "accepted");
  };

  // Calculate statistics from all bids (before filtering)
  const statistics = {
    total: allBids.length,
    active: allBids.filter((b) => b.bid_status === "active" || b.bid_status === "pending").length,
    won: allBids.filter((b) => isWonBid(b)).length,
    lost: allBids.filter((b) => b.bid_status === "lost" || b.bid_status === "outbid").length,
    rejected: allBids.filter((b) => b.bid_status === "rejected").length,
    totalValue: allBids.reduce((sum, b) => {
      const amount = parseFloat(b.bid_amount) || 0;
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0),
  };

  return (
    <div className="my-bids-page">
      <div className="container">
        <div className="page-header">
          <h1>My Bids</h1>
          <p>Track all your bids and auction participation</p>
        </div>

        {/* Statistics */}
        <div className="statistics">
          <div className="stat-card">
            <div className="stat-icon">🔨</div>
            <div className="stat-value">{statistics.total}</div>
            <div className="stat-label">Total Bids</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⏰</div>
            <div className="stat-value">{statistics.active}</div>
            <div className="stat-label">Active Bids</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✓</div>
            <div className="stat-value">{statistics.won}</div>
            <div className="stat-label">Won Auctions</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">₹</div>
            <div className="stat-value">{formatCurrency(statistics.totalValue)}</div>
            <div className="stat-label">Total Bid Value</div>
          </div>
        </div>

        {/* Filters */}
        <div className="filters">
          <button
            className={filterStatus === "all" ? "active" : ""}
            onClick={() => setFilterStatus("all")}
          >
            All Bids ({statistics.total})
          </button>
          <button
            className={filterStatus === "active" ? "active" : ""}
            onClick={() => setFilterStatus("active")}
          >
            Active ({statistics.active})
          </button>
          <button
            className={filterStatus === "won" ? "active" : ""}
            onClick={() => setFilterStatus("won")}
          >
            Won ({statistics.won})
          </button>
          <button
            className={filterStatus === "lost" ? "active" : ""}
            onClick={() => setFilterStatus("lost")}
          >
            Lost ({statistics.lost})
          </button>
          <button
            className={filterStatus === "rejected" ? "active" : ""}
            onClick={() => setFilterStatus("rejected")}
          >
            Rejected ({statistics.rejected})
          </button>
        </div>

        {/* Bids List */}
        {loading ? (
          <div className="loading">Loading bids...</div>
        ) : bids.length === 0 ? (
          <div className="no-bids">
            <div className="empty-icon">📦</div>
            <h2>No Bids Found</h2>
            <p>You haven't placed any bids yet</p>
            <button
              className="btn-primary"
              onClick={() => navigate("/auction")}
            >
              Browse Auctions
            </button>
          </div>
        ) : (
          <div className="bids-list">
            {bids.map((bid) => (
              <div key={bid.bid_id} className="bid-item">
                <div className="bid-info">
                  <div className="bid-header">
                    <h3>{bid.product_name || "Auction Item"}</h3>
                    {getBidStatusBadge(bid.bid_status, bid.is_highest_bid)}
                  </div>
                  <div className="bid-details">
                    <div className="detail">
                      <span className="label">Your Bid:</span>
                      <span className="value highlight">
                        {formatCurrency(bid.bid_amount)}
                      </span>
                    </div>
                    <div className="detail">
                      <span className="label">Auction Status:</span>
                      <span className="value">
                        {getAuctionStatus(bid)}
                      </span>
                    </div>
                    <div className="detail">
                      <span className="label">Bid Placed:</span>
                      <span className="value">
                        {bid.bid_placed_at 
                          ? new Date(bid.bid_placed_at).toLocaleString()
                          : bid.created_at 
                          ? new Date(bid.created_at).toLocaleString()
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="bid-actions">
                  <button
                    className="btn-view"
                    onClick={() => navigate(`/auction/${bid.auction_id}`)}
                  >
                    View Auction
                  </button>
                  {isWonBid(bid) && bid.payment_status !== "completed" && (
                    <button
                      className="btn-primary"
                      onClick={() => navigate(`/auction/${bid.auction_id}/payment`)}
                    >
                      Complete Payment
                    </button>
                  )}
                  {isWonBid(bid) && bid.payment_status === "completed" && (
                    <>
                      {/* Show "Send Request to QA" only when Inspection Allowed = Yes and Inspector Type = Private Inspector */}
                      {bid.inspection_allowed === "Yes" &&
                        bid.inspector_type === "Private Inspector" &&
                        (!inspectionRFQStatus[bid.auction_id] ||
                          inspectionRFQStatus[bid.auction_id] === "pending" ||
                          inspectionRFQStatus[bid.auction_id] === "draft") && (
                        <button
                          className="btn-primary"
                          onClick={() => navigate(`/auction/${bid.auction_id}/quality-evaluator-prompt`)}
                        >
                          Send Request to QA
                        </button>
                      )}
                      {/* Show "View QA Quotations" only when quotations exist and inspector not yet selected (before Review & Accept is done) */}
                      {(inspectionQuotesCount[bid.auction_id] > 0 &&
                        inspectionRFQStatus[bid.auction_id] !== "inspector_selected" &&
                        inspectionRFQStatus[bid.auction_id] !== "inspection_scheduled" &&
                        inspectionRFQStatus[bid.auction_id] !== "completed") && (
                        <button
                          className="btn-primary"
                          onClick={() => navigate(`/auction/${bid.auction_id}/review-quotations`)}
                        >
                          View QA Quotations
                        </button>
                      )}
                      {/* Show "View Inspection" when inspector selected or inspection in progress */}
                      {(inspectionRFQStatus[bid.auction_id] === 'inspector_selected' || 
                        inspectionRFQStatus[bid.auction_id] === 'inspection_scheduled') && (
                        <button
                          className="btn-primary"
                          onClick={() => navigate(`/auction/${bid.auction_id}/inspection-initiated`)}
                        >
                          {inspectionP2pPayment[bid.auction_id]?.can_show_view_inspection_pay_now
                            ? 'View Inspection & Pay Now'
                            : 'View Inspection'}
                        </button>
                      )}
                      {/* After inspection is completed, show Accept/Reject only if buyer has not decided yet */}
                      {inspectionRFQStatus[bid.auction_id] === 'completed' && !inspectionDecisionByAuction[bid.auction_id] && (
                        <button
                          className="btn-primary"
                          onClick={() => navigate(`/auction/${bid.auction_id}/accept-reject-product`)}
                        >
                          Accept / Reject Product
                        </button>
                      )}
                      {/* Product Collection Status: for QA flow only after inspection completed and buyer accepted; for non-QA flow show when payment completed */}
                      {showProductCollectionStatus(bid) && (
                        <button
                          className="btn-collection"
                          onClick={() => navigate(`/auction/${bid.auction_id}/collection-delivery`)}
                        >
                          {bid.collection_status === "confirmed" 
                            ? "View Product Collection Status" 
                            : "Update Collection Status"}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
